'use client';

import React, { useState, useRef, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { ChevronDown, Search, MoveHorizontal, Star } from 'lucide-react';
import SocialShare from './SocialShare';
import { Converter, Unit } from '@/lib/types/converter';
import { trackConversion } from '@/lib/dataService';

interface ConverterCardProps {
  converter: Converter;
}

export function ConverterCard({ converter }: ConverterCardProps) {
  const [fromValue, setFromValue] = useState<string>('1');
  const [fromUnit, setFromUnit] = useState<Unit | null>(converter.units[0] || null);
  const [toUnit, setToUnit] = useState<Unit | null>(converter.units[1] || converter.units[0] || null);
  const [fromSearch, setFromSearch] = useState('');
  const [toSearch, setToSearch] = useState('');
  const [isFromOpen, setIsFromOpen] = useState(false);
  const [isToOpen, setIsToOpen] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showAllUnits, setShowAllUnits] = useState(false);

  const fromRef = useRef<HTMLDivElement>(null);
  const toRef = useRef<HTMLDivElement>(null);
  const Icon = LucideIcons[converter.icon as keyof typeof LucideIcons] as React.ComponentType<any>;

  useEffect(() => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    setIsFavorite(favorites.includes(converter.id));
  }, [converter.id]);

  const toggleFavorite = () => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    let newFavorites;
    if (favorites.includes(converter.id)) {
      newFavorites = favorites.filter((id: string) => id !== converter.id);
      setIsFavorite(false);
    } else {
      newFavorites = [...favorites, converter.id];
      setIsFavorite(true);
    }
    localStorage.setItem('favorites', JSON.stringify(newFavorites));
    window.dispatchEvent(new Event('favoritesChanged'));
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const fromName = searchParams.get('from');
    const toName = searchParams.get('to');

    if (fromName) {
      const unit = converter.units.find((u) => u.name === decodeURIComponent(fromName));
      if (unit) setFromUnit(unit);
    }

    if (toName) {
      const unit = converter.units.find((u) => u.name === decodeURIComponent(toName));
      if (unit) setToUnit(unit);
    }
  }, [converter]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!fromRef.current?.contains(event.target as Node)) setIsFromOpen(false);
      if (!toRef.current?.contains(event.target as Node)) setIsToOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const convert = (value: number, from: Unit, to: Unit): string => {
    let result: number;
    if (from.offset !== undefined && to.offset !== undefined) {
      const baseValue = value + (from.offset || 0);
      result = (baseValue * from.factor) / to.factor - (to.offset || 0);
    } else {
      result = (value * from.factor) / to.factor;
    }
    
    const hasDecimals = result % 1 !== 0;
    if (hasDecimals) {
      const str = result.toString();
      const decimalIndex = str.indexOf('.');
      if (decimalIndex !== -1) {
        const decimalPart = str.slice(decimalIndex + 1);
        const nonZeroIndex = decimalPart.split('').findIndex((c) => c !== '0');
        const decimalsToShow = Math.min(nonZeroIndex + 2, 8);
        return result.toFixed(decimalsToShow);
      }
    }
    return result.toFixed(2);
  };

  const handleSwap = () => {
    if (fromUnit && toUnit) {
      const temp = fromUnit;
      setFromUnit(toUnit);
      setToUnit(temp);
    }
  };

  const handleFromValueChange = (value: string) => {
    setFromValue(value);
    if (fromUnit && toUnit) trackConversion(fromUnit.symbol, toUnit.symbol);
  };

  const filteredFromUnits = converter.units.filter(unit =>
    unit.name.toLowerCase().includes(fromSearch.toLowerCase()) ||
    unit.symbol.toLowerCase().includes(fromSearch.toLowerCase())
  );

  const filteredToUnits = converter.units.filter(unit =>
    unit.name.toLowerCase().includes(toSearch.toLowerCase()) ||
    unit.symbol.toLowerCase().includes(toSearch.toLowerCase())
  );

  if (!converter.units || converter.units.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border p-6 relative">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-50 p-2 rounded-lg">
              {Icon && <Icon className="w-6 h-6 text-emerald-600" />}
            </div>
            <h2 className="text-xl font-bold text-gray-900">{converter.name}</h2>
          </div>
          <SocialShare />
        </div>
        <p className="text-gray-500">Units coming soon...</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6 relative overflow-hidden">
      {/* Decorative background */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50 rounded-bl-full -mr-16 -mt-16 opacity-50 z-0" />
      
      <div className="relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="bg-emerald-100 p-2 rounded-xl">
              {Icon && <Icon className="w-6 h-6 text-emerald-600" />}
            </div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-gray-900">{converter.name}</h2>
              <button
                onClick={toggleFavorite}
                className={`p-1.5 rounded-lg transition-all hover:scale-110 ${
                  isFavorite ? 'text-amber-400 bg-amber-50' : 'text-gray-300 hover:text-amber-400 hover:bg-amber-50'
                }`}
                aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
              >
                <Star className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
              </button>
            </div>
          </div>
          <SocialShare />
        </div>

        {fromValue && fromUnit && toUnit && (
          <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3 mb-6 text-center">
            <p className="text-emerald-900 font-bold text-lg">
              {fromValue} <span className="text-sm font-normal text-emerald-600">{fromUnit.name}</span> = {convert(parseFloat(fromValue), fromUnit, toUnit)} <span className="text-sm font-normal text-emerald-600">{toUnit.name}</span>
            </p>
          </div>
        )}

        <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
          <div className="flex-1 w-full" ref={fromRef}>
            <div className="relative group">
              <input
                id="fromValue"
                type="number"
                value={fromValue}
                onChange={(e) => handleFromValueChange(e.target.value)}
                className="w-full pl-4 pr-4 py-3 border-2 rounded-xl focus:ring-4 focus:ring-emerald-500/10 focus:border-emerald-500 outline-none transition-all text-lg font-semibold"
                placeholder="0"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">{fromUnit?.symbol}</span>
            </div>
            
            <div className="relative mt-2">
              <button
                onClick={() => setIsFromOpen(!isFromOpen)}
                className="w-full px-4 py-3 border rounded-xl flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium"
              >
                <span className="truncate">{fromUnit?.name || 'Select Unit'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isFromOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isFromOpen && (
                <div className="absolute z-50 w-full mt-2 bg-white border rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2">
                  <div className="p-2 border-b bg-gray-50">
                    <div className="relative">
                      <input
                        type="text"
                        autoFocus
                        value={fromSearch}
                        onChange={(e) => setFromSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500/20"
                        placeholder="Search units..."
                      />
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredFromUnits.map((unit) => (
                      <button
                        key={unit.symbol}
                        onClick={() => { setFromUnit(unit); setIsFromOpen(false); }}
                        className={`w-full px-4 py-3 text-left hover:bg-emerald-50 flex justify-between items-center transition-colors ${
                          fromUnit?.symbol === unit.symbol ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-sm">{unit.name}</span>
                        <span className="text-xs opacity-50 font-mono">{unit.symbol}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <button
            onClick={handleSwap}
            className="p-3 bg-gray-100 hover:bg-emerald-600 hover:text-white rounded-xl transition-all shadow-sm active:scale-95 group"
            aria-label="Swap units"
          >
            <MoveHorizontal className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
          </button>

          <div className="flex-1 w-full" ref={toRef}>
            <div className="relative">
              <input
                id="toValue"
                type="text"
                value={fromValue && fromUnit && toUnit ? convert(parseFloat(fromValue), fromUnit, toUnit) : '0'}
                readOnly
                className="w-full pl-4 pr-4 py-3 border-2 bg-gray-50 rounded-xl text-lg font-semibold text-emerald-700"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 font-medium">{toUnit?.symbol}</span>
            </div>

            <div className="relative mt-2">
              <button
                onClick={() => setIsToOpen(!isToOpen)}
                className="w-full px-4 py-3 border rounded-xl flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors text-sm font-medium"
              >
                <span className="truncate">{toUnit?.name || 'Select Unit'}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${isToOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isToOpen && (
                <div className="absolute z-50 w-full mt-2 bg-white border rounded-xl shadow-xl overflow-hidden animate-in fade-in slide-in-from-top-2">
                  <div className="p-2 border-b bg-gray-50">
                    <div className="relative">
                      <input
                        type="text"
                        autoFocus
                        value={toSearch}
                        onChange={(e) => setToSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-emerald-500/20"
                        placeholder="Search units..."
                      />
                      <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
                    </div>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {filteredToUnits.map((unit) => (
                      <button
                        key={unit.symbol}
                        onClick={() => { setToUnit(unit); setIsToOpen(false); }}
                        className={`w-full px-4 py-3 text-left hover:bg-emerald-50 flex justify-between items-center transition-colors ${
                          toUnit?.symbol === unit.symbol ? 'bg-emerald-50 text-emerald-700 font-semibold' : 'text-gray-700'
                        }`}
                      >
                        <span className="text-sm">{unit.name}</span>
                        <span className="text-xs opacity-50 font-mono">{unit.symbol}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="mt-8 border-t pt-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-xs text-gray-400 uppercase font-bold tracking-wider">All {converter.name} Units</p>
            {converter.units.length > 8 && (
              <button 
                onClick={() => setShowAllUnits(!showAllUnits)}
                className="text-xs text-emerald-600 font-bold hover:underline"
              >
                {showAllUnits ? 'Show Less' : `Show All ${converter.units.length} Units`}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {(showAllUnits ? converter.units : converter.units.slice(0, 8)).map((unit) => (
              <button
                key={unit.symbol}
                onClick={() => setFromUnit(unit)}
                className={`px-3 py-2 text-xs rounded-lg border transition-all hover:border-emerald-500 hover:text-emerald-600 ${
                  fromUnit?.symbol === unit.symbol ? 'border-emerald-500 bg-emerald-50 text-emerald-700 font-bold' : 'bg-white text-gray-600'
                }`}
              >
                {unit.name}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
